/**
 * Builds the language model every request runs on: the Anvil backend,
 * which forwards to OpenRouter.
 */

import path from 'path'

import { BYOK_OPENROUTER_HEADER } from '@anvil/common/constants/byok'
import {
  DEEPSEEK_BASE_URL,
  getDirectDeepSeekApiKey,
  remapModelForDirectDeepSeek,
} from '@anvil/common/constants/deepseek-direct'
import { FREEBUFF_ACTING_USER_HEADER } from '@anvil/common/constants/freebuff-models'
import { isTransientNetworkError } from '@anvil/common/util/error'
import {
  OpenAICompatibleChatLanguageModel,
  VERSION,
} from '@anvil/llm-providers/openai-compatible'
import { APICallError } from 'ai'

import { getWebsiteUrl } from '../constants'
import { getByokOpenrouterApiKeyFromEnv } from '../env'

import type { LanguageModel } from 'ai'

/**
 * Parameters for requesting a model.
 */
export interface ModelRequestParams {
  /** Anvil API key for backend authentication */
  apiKey: string
  /** Model ID (OpenRouter format, e.g., "anthropic/claude-sonnet-4") */
  model: string
  /** End user represented by a trusted service-account request. */
  userId?: string
}

// Usage accounting type for OpenRouter/Anvil backend responses
type OpenRouterUsageAccounting = {
  cost: number | null
  costDetails: {
    upstreamInferenceCost: number | null
  }
}

/**
 * Notification hook for free-mode capacity deferrals. When the backend sheds
 * a free-mode completion under saturation (HTTP 429 with
 * `error: 'free_mode_capacity_deferred'` — see the server's
 * free-mode-priority.ts), the AI SDK's retry loop absorbs the wait silently.
 * Hosts (the CLI) can register here to surface a "high demand" indicator
 * instead of an unexplained pause.
 */
export type FreeModeCapacityDeferral = { retryAfterSeconds: number }

let freeModeCapacityDeferralListener:
  | ((deferral: FreeModeCapacityDeferral) => void)
  | null = null

export function setFreeModeCapacityDeferralListener(
  listener: ((deferral: FreeModeCapacityDeferral) => void) | null,
): void {
  freeModeCapacityDeferralListener = listener
}

function notifyCapacityDeferralFromResponse(response: Response): void {
  if (response.status !== 429 || !freeModeCapacityDeferralListener) return
  // Clone so the AI SDK still reads the original body for its own error
  // handling/retry. Both the parse and the listener are best-effort: a
  // malformed body or throwing listener must never break the request path.
  void response
    .clone()
    .json()
    .then((body: unknown) => {
      const error =
        body && typeof body === 'object' ? (body as any).error : undefined
      if (error !== 'free_mode_capacity_deferred') return
      const retryAfterHeader = Number(response.headers.get('retry-after'))
      freeModeCapacityDeferralListener?.({
        retryAfterSeconds:
          Number.isFinite(retryAfterHeader) && retryAfterHeader > 0
            ? retryAfterHeader
            : 10,
      })
    })
    .catch(() => {})
}

/**
 * Wrap global fetch so transient connection failures (socket closed/reset,
 * connection refused) are rethrown as retryable APICallErrors.
 *
 * Bun's fetch throws these as plain Errors ("The socket connection was closed
 * unexpectedly...", code ECONNRESET/ConnectionClosed), which the AI SDK does
 * not recognize as retryable — it only auto-retries APICallError with
 * isRetryable=true. Marking them retryable lets streamText's built-in
 * exponential backoff (default 2 retries) absorb brief server/network blips
 * instead of failing the whole agent run.
 */
function fetchWithRetryableNetworkErrors(
  ...args: Parameters<typeof globalThis.fetch>
): ReturnType<typeof globalThis.fetch> {
  return globalThis
    .fetch(...args)
    .then((response) => {
      notifyCapacityDeferralFromResponse(response)
      return response
    })
    .catch((error: unknown) => {
      if (isTransientNetworkError(error)) {
        const input = args[0]
        const url =
          typeof input === 'string'
            ? input
            : input instanceof URL
              ? input.toString()
              : input.url
        throw new APICallError({
          message: error instanceof Error ? error.message : String(error),
          cause: error,
          url,
          requestBodyValues: {},
          isRetryable: true,
        })
      }
      throw error
    })
}

/**
 * Get the model for a request.
 *
 * Default: routes through the Anvil backend, which forwards to OpenRouter.
 * When `DEEPSEEK_API_KEY` is set (direct-to-DeepSeek mode), calls DeepSeek's
 * OpenAI-compatible API directly instead. The provider is named `deepseek` so
 * Anvil-specific provider options (`anvil_metadata`) are never spread
 * into the request body DeepSeek receives.
 */
export function getModelForRequest({
  apiKey,
  model,
  userId,
}: ModelRequestParams): LanguageModel {
  const directDeepSeekApiKey = getDirectDeepSeekApiKey()
  if (directDeepSeekApiKey) {
    return getDeepSeekDirectModel({
      apiKey: directDeepSeekApiKey,
      model,
    })
  }

  const openrouterUsage: OpenRouterUsageAccounting = {
    cost: null,
    costDetails: {
      upstreamInferenceCost: null,
    },
  }

  const openrouterApiKey = getByokOpenrouterApiKeyFromEnv()

  return new OpenAICompatibleChatLanguageModel(model, {
    provider: 'anvil',
    url: ({ path: endpoint }) =>
      new URL(path.join('/api/v1', endpoint), getWebsiteUrl()).toString(),
    headers: () => ({
      Authorization: `Bearer ${apiKey}`,
      'user-agent': `ai-sdk/openai-compatible/${VERSION}/anvil`,
      ...(userId ? { [FREEBUFF_ACTING_USER_HEADER]: userId } : {}),
      ...(openrouterApiKey && { [BYOK_OPENROUTER_HEADER]: openrouterApiKey }),
    }),
    metadataExtractor: {
      extractMetadata: async ({ parsedBody }: { parsedBody: any }) => {
        if (openrouterApiKey !== undefined) {
          return { anvil: { usage: openrouterUsage } }
        }

        if (typeof parsedBody?.usage?.cost === 'number') {
          openrouterUsage.cost = parsedBody.usage.cost
        }
        if (
          typeof parsedBody?.usage?.cost_details?.upstream_inference_cost ===
          'number'
        ) {
          openrouterUsage.costDetails.upstreamInferenceCost =
            parsedBody.usage.cost_details.upstream_inference_cost
        }
        return { anvil: { usage: openrouterUsage } }
      },
      createStreamExtractor: () => ({
        processChunk: (parsedChunk: any) => {
          if (openrouterApiKey !== undefined) {
            return
          }

          if (typeof parsedChunk?.usage?.cost === 'number') {
            openrouterUsage.cost = parsedChunk.usage.cost
          }
          if (
            typeof parsedChunk?.usage?.cost_details?.upstream_inference_cost ===
            'number'
          ) {
            openrouterUsage.costDetails.upstreamInferenceCost =
              parsedChunk.usage.cost_details.upstream_inference_cost
          }
        },
        buildMetadata: () => {
          return { anvil: { usage: openrouterUsage } }
        },
      }),
    },
    // Cast: Bun's fetch type also declares a `preconnect` helper, but the AI
    // SDK only ever invokes fetch as a plain function.
    fetch: fetchWithRetryableNetworkErrors as typeof globalThis.fetch,
    includeUsage: undefined,
    supportsStructuredOutputs: true,
  })
}

/**
 * Build the language model for direct-to-DeepSeek mode: the OpenAI-compatible
 * model pointed at DeepSeek's own API, authenticated with the user's DeepSeek
 * key.
 *
 * The provider is named `deepseek` (not `anvil`) so that the Anvil
 * provider options (`anvil_metadata`, provider routing, etc.) that
 * `getProviderOptions` attaches are never spread into the request body — the
 * OpenAI-compatible chat model only forwards `providerOptions[providerName]`,
 * and nothing is registered under `deepseek`.
 */
function getDeepSeekDirectModel({
  apiKey,
  model,
}: {
  apiKey: string
  model: string
}): LanguageModel {
  return new OpenAICompatibleChatLanguageModel(
    remapModelForDirectDeepSeek(model),
    {
      provider: 'deepseek',
      url: ({ path: endpoint }) =>
        new URL(path.join('/v1', endpoint), DEEPSEEK_BASE_URL).toString(),
      headers: () => ({
        Authorization: `Bearer ${apiKey}`,
        'user-agent': `ai-sdk/openai-compatible/${VERSION}/deepseek-direct`,
      }),
      // DeepSeek's OpenAI-compatible API accepts `json_object` response
      // format but not `json_schema`, so structured output calls fall back to
      // JSON mode instead of being rejected.
      supportsStructuredOutputs: false,
      // Cast: Bun's fetch type also declares a `preconnect` helper, but the AI
      // SDK only ever invokes fetch as a plain function.
      fetch: fetchWithRetryableNetworkErrors as typeof globalThis.fetch,
      includeUsage: undefined,
    },
  )
}
